import { useEffect, useRef } from "react";
import { Link, withRouter } from "react-router-dom";
import { aboutMenuData } from "./data/aboutMenuData";
import { growData } from "./data/enterpriseMenuData";

// This section sub-nav's Resources tab set is curated separately from
// enterpriseMenuData (which drives the header's Resources mega-menu and
// mobile nav) — the two lists intentionally differ, so changing one must
// not change the other.
const resourcesSubNavItems = [
  { id: 1, title: "Resources Home", url: "/blog" },
  { id: 2, title: "Events", url: "/events" },
  { id: 4, title: "Talent Stories", url: "/stories" },
  { id: 5, title: "Scholarships", url: "/scholarship" },
  { id: 6, title: "Podcast", url: "/podcast" },
];

const groups = [
  {
    key: "about",
    label: "About Utiva",
    pillLabel: "Company",
    rootUrl: "/about-us",
    items: aboutMenuData,
  },
  {
    key: "grow",
    label: "Grow with Utiva",
    pillLabel: "Grow with Utiva",
    rootUrl: "/business",
    items: growData,
  },
  {
    key: "resources",
    label: "Resources",
    pillLabel: "Resources",
    rootUrl: "/blog",
    items: resourcesSubNavItems,
  },
];

// Some pages inside a group show their own local sub-navigation instead of
// the full family list — e.g. Careers (and its child Be a Trainer page)
// share a Life at Utiva / Open Roles / Be a Trainer set rather than the
// whole About Utiva family. The two section links are same-page anchors
// when already on /careers, but need the full path when viewed from a
// different page in the set (e.g. from /be-a-trainer).
const careersItems = (pathname) => {
  const onCareersPage = pathname === "/careers";
  return [
    { id: 1, title: "Careers", url: "/careers" },
    {
      id: 2,
      title: "Life at Utiva",
      url: onCareersPage ? "#life" : "/careers#life",
    },
    {
      id: 3,
      title: "Open Roles",
      url: onCareersPage ? "#roles" : "/careers#roles",
    },
    { id: 4, title: "Be a Trainer", url: "/be-a-trainer" },
  ];
};

const pageOverrides = {
  "/careers": { label: "Careers", items: careersItems },
  "/be-a-trainer": { label: "Careers", items: careersItems },
  // Events, Stories and Podcast show the full Resources family list, just
  // under the family name rather than their own page title.
  "/events": { label: "Resources" },
  "/stories": { label: "Resources", group: "resources" },
  "/podcast": { label: "Resources" },
  // Corporate Training (Consulting Practice) belongs to the Resources
  // family but, like the pages above, isn't itself one of the tabs — it
  // needs an explicit `group` since its path has no matching item for
  // findActiveGroup to key off.
  "/corporate-training": { label: "Resources", group: "resources" },
};

const stripHash = (url) => url?.split("#")[0];

// Hash-anchored items (in-page scroll links, e.g. "/careers#roles") never
// register as active — only a plain, non-anchor URL match counts, so
// scrolling to a section doesn't light up every anchor on the same page.
const isItemActive = (item, pathname) =>
  !item?.url?.includes("http") &&
  !item?.url?.includes("#") &&
  stripHash(item.url) === pathname;

const findActiveGroup = (pathname) => {
  const overrideGroupKey = pageOverrides[pathname]?.group;
  if (overrideGroupKey) {
    return groups.find((group) => group.key === overrideGroupKey);
  }
  return groups.find((group) =>
    group.items.some((item) => isItemActive(item, pathname))
  );
};

const TabLink = ({ item, isActive }) => {
  const isExternal = item?.url?.includes("http");
  const isAnchor = item?.url?.includes("#");
  const className = `flex-none inline-flex items-center whitespace-nowrap rounded-[10px] border min-h-[40px] sm:min-h-[43px] px-[11px] sm:px-[13px] py-[9px] sm:py-[10px] font-poppins text-[14px] sm:text-[15px] transition-colors ${
    isActive
      ? "border-secondary bg-white text-primary font-extrabold"
      : "border-transparent text-[#596177] font-bold hover:text-primary hover:bg-white hover:border-[#cdd8ef]"
  }`;
  const style = isActive
    ? {
        boxShadow:
          "inset 0 -3px 0 var(--color-secondary), 0 7px 18px rgba(30,38,76,0.06)",
      }
    : undefined;

  if (isExternal) {
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={style}
      >
        {item.title}
      </a>
    );
  }

  // Links with a hash (same-page anchors like "#roles", or a different
  // page + anchor like "/careers#life") use a plain <a> so the browser's
  // native fragment scroll runs on load — react-router's Link only pushes
  // history and never scrolls to a hash.
  if (isAnchor) {
    return (
      <a href={item.url} className={className} style={style}>
        {item.title}
      </a>
    );
  }

  return (
    <Link to={item.url} className={className} style={style}>
      {item.title}
    </Link>
  );
};

const GroupSubNav = ({ location }) => {
  const pathname = location.pathname;
  const activeGroup = findActiveGroup(pathname);
  const shellRef = useRef(null);

  useEffect(() => {
    if (!activeGroup || !shellRef.current) return;

    const measure = () => {
      if (shellRef.current) {
        document.documentElement.style.setProperty(
          "--subnav-height",
          `${shellRef.current.offsetHeight}px`
        );
      }
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  });

  if (!activeGroup) return null;

  const override = pageOverrides[pathname];
  const label =
    override?.label ??
    (pathname === activeGroup.rootUrl
      ? activeGroup.label
      : activeGroup.items.find((item) => isItemActive(item, pathname))?.title ??
        activeGroup.label);
  const items = override?.items ? override.items(pathname) : activeGroup.items;

  return (
    // A Fragment, not a wrapping div: position:sticky on the <nav> below
    // needs its containing block to span the full scrollable page (header
    // through footer), not a box sized to the sub-nav's own ~70px height —
    // a snug wrapper leaves it no room to stay pinned while the rest of
    // the page scrolls underneath it.
    <>
      {activeGroup.key === "grow" && (
        <div className="bg-hire-tint-blue border-b border-hire-line">
          <div className="container mx-auto px-4">
            <div className="flex flex-nowrap gap-3 pt-4 pb-4 overflow-x-auto no-scrollbar">
              {groups.map((group) => {
                const isOpen = group.key === activeGroup.key;
                return (
                  <Link
                    key={group.key}
                    to={group.rootUrl}
                    className={`flex flex-none items-center gap-3 rounded-lg border px-6 py-3 font-poppins text-sm font-extrabold transition-colors focus:outline-none ${
                      isOpen
                        ? "bg-primary border-primary text-white"
                        : "bg-white border-hire-line text-default hover:border-primary"
                    }`}
                  >
                    {group.pillLabel}
                    <span
                      className={`text-base leading-none ${
                        isOpen ? "text-secondary" : "text-primary"
                      }`}
                      aria-hidden="true"
                    >
                      {isOpen ? "−" : "+"}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <nav
        ref={shellRef}
        aria-label={`${activeGroup.label} section navigation`}
        className="z-20 w-full border-b border-[#dce4f5]"
        style={{
          background: "#f4f7ff",
          boxShadow: "0 8px 22px rgba(22,31,77,0.04)",
          position: "sticky",
          top: "var(--header-height, 90px)",
        }}
      >
        <div className="flex flex-nowrap items-center gap-[11px] sm:gap-[17px] w-full max-w-[1280px] min-h-[62px] sm:min-h-[68px] mx-auto px-4 sm:px-[26px] py-[7px] sm:py-2 overflow-x-auto no-scrollbar">
          <div className="flex-none grid gap-[1px] min-w-[105px] sm:min-w-[145px]">
            <span className="text-[#8c5b00] font-poppins tracking-[0.09em] text-[11px] sm:text-[12px] font-extrabold uppercase">
              You are in
            </span>
            <strong className="text-primary font-poppins text-[15px] sm:text-[17px] font-extrabold whitespace-nowrap">
              {label}
            </strong>
          </div>

          <span
            className="block flex-none bg-secondary rounded-full h-[30px] sm:h-[36px]"
            style={{ width: "3px" }}
            aria-hidden="true"
          ></span>

          <div className="flex flex-nowrap items-center gap-[6px] min-w-0">
            {items.map((item) => (
              <TabLink
                key={item.id}
                item={item}
                isActive={isItemActive(item, pathname)}
              />
            ))}
          </div>
        </div>
      </nav>
    </>
  );
};

export default withRouter(GroupSubNav);
